/**
 * VisualGest — Área de retenção de anulações (Fase O.8 — 2026-05-14).
 *
 * Quando o VisualGest comunica uma anulação de documento, o stock NÃO é
 * revertido automaticamente — fica aqui em retenção e o utilizador decide:
 *   - Repor todas as unidades
 *   - Repor parcialmente N unidades
 *   - Ignorar (manter stock como está)
 *
 * Espelha a lógica da área de retenção do Moloni. Backend:
 *   POST /api/v2/olifel/pending-actions          (lista)
 *   POST /api/v2/olifel/pending-actions/resolve  (resolve)
 */

import React, { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    getOlifelPendingActions,
    resolveOlifelPendingAction,
} from "../../services/OlifelServices";

const STATUS_LABELS = {
    pending: "Pendente",
    resolved_restored: "Reposto (total)",
    resolved_partial: "Reposto (parcial)",
    resolved_skipped: "Ignorado",
};

const STATUS_FILTERS = [
    { value: "", label: "Todos os estados" },
    { value: "pending", label: "Pendentes" },
    { value: "resolved_restored", label: "Repostos (total)" },
    { value: "resolved_partial", label: "Repostos (parcial)" },
    { value: "resolved_skipped", label: "Ignorados" },
];

const PAGE_SIZE = 25;

export default function VisualGestStockAlerts() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState("pending");
    const [loading, setLoading] = useState(false);
    const [resolvingId, setResolvingId] = useState(null);
    // Quantidade do "repor parcial" por linha (id → qty).
    const [partialQty, setPartialQty] = useState({});

    const load = async (targetPage = page) => {
        try {
            setLoading(true);
            const r = await getOlifelPendingActions({
                status: statusFilter || undefined,
                page: targetPage,
                limit: PAGE_SIZE,
            });
            const data = r.data || {};
            setRows(data.actions || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(
                (err && err.response && err.response.data && err.response.data.message) ||
                "Erro ao carregar anulações em retenção"
            );
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(1);
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const handlePage = (e) => {
        const next = e.selected + 1;
        setPage(next);
        load(next);
    };

    const resolve = async (row, action) => {
        let payload = { id: row.id, action };
        if (action === "partial") {
            const qty = parseInt(partialQty[row.id], 10);
            if (!qty || qty < 1 || qty > (row.available_qty || 0)) {
                toast.error(`Indique uma quantidade entre 1 e ${row.available_qty || 0}`);
                return;
            }
            payload.partial_qty = qty;
        }
        try {
            setResolvingId(row.id);
            const r = await resolveOlifelPendingAction(payload);
            const data = r.data || {};
            if (data.success) {
                toast.success(data.message || "Anulação resolvida");
                load();
            } else {
                toast.error(data.message || "Não foi possível resolver");
            }
        } catch (err) {
            toast.error(
                (err && err.response && err.response.data && err.response.data.message) ||
                "Erro ao resolver anulação"
            );
        } finally {
            setResolvingId(null);
        }
    };

    const docRef = (row) =>
        row.document_full_id || row.inbound_sale_full_id || `#${row.o_document_id || row.o_inbound_sale_id || "?"}`;

    return (
        <div>
            <section className="content-header">
                <div className="container-fluid container-custom-height-new">
                    <div className="row mb-2">
                        <div className="col-sm-12 d-flex align-items-center">
                            <button
                                type="button"
                                className="next-button btn btn-secondary"
                                onClick={() => navigate("/visualgest")}
                            >
                                <i className="bi bi-arrow-left"></i> {t("back", { defaultValue: "Voltar" })}
                            </button>
                            <h1 className="page-title ms-auto mb-2">
                                {t("visualgest_stock_alerts_title", {
                                    defaultValue: "VisualGest — Anulações em retenção",
                                })}
                            </h1>
                        </div>
                    </div>
                </div>
            </section>

            <section className="content">
                <div className="container-fluid container-custom-height-new">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="card fuelCard">
                                <div className="card-header">
                                    <div className="card-title m-0 float-left" style={{ width: "50%" }}>
                                        <select
                                            className="form-control input-search"
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                        >
                                            {STATUS_FILTERS.map((o) => (
                                                <option key={o.value} value={o.value}>{o.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="card-title m-0 float-right d-flex">
                                        <button
                                            type="button"
                                            className="btn btn-icon-text mr-2"
                                            onClick={() => load()}
                                            disabled={loading}
                                        >
                                            <i className="bi bi-arrow-repeat"></i>
                                            <span className="ms-2">{t("refresh", { defaultValue: "Actualizar" })}</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="card-body fuelCardB">
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-hover">
                                            <thead>
                                                <tr>
                                                    <th>{t("document", { defaultValue: "Documento" })}</th>
                                                    <th>{t("type", { defaultValue: "Tipo" })}</th>
                                                    <th>{t("part", { defaultValue: "Peça" })}</th>
                                                    <th>{t("requested", { defaultValue: "Pedido" })}</th>
                                                    <th>{t("available", { defaultValue: "Disponível" })}</th>
                                                    <th>{t("status", { defaultValue: "Estado" })}</th>
                                                    <th>{t("date", { defaultValue: "Data" })}</th>
                                                    <th style={{ minWidth: 280 }}>{t("actions", { defaultValue: "Acções" })}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading && (
                                                    <tr><td colSpan={8} className="text-center">A carregar…</td></tr>
                                                )}
                                                {!loading && rows.length === 0 && (
                                                    <tr>
                                                        <td colSpan={8} className="text-center">
                                                            {t("no_records", { defaultValue: "Sem registos" })}
                                                        </td>
                                                    </tr>
                                                )}
                                                {!loading && rows.map((row) => (
                                                    <tr key={row.id}>
                                                        <td>{docRef(row)}</td>
                                                        <td>
                                                            {row.action_type === "cancellation"
                                                                ? "Anulação"
                                                                : "Documento eliminado"}
                                                        </td>
                                                        <td>
                                                            {row.parts_internal_id || "—"}
                                                            {row.part_name ? ` · ${row.part_name}` : ""}
                                                        </td>
                                                        <td>{row.requested_qty != null ? row.requested_qty : "—"}</td>
                                                        <td>{row.available_qty != null ? row.available_qty : "—"}</td>
                                                        <td>{STATUS_LABELS[row.status] || row.status}</td>
                                                        <td>
                                                            {row.created_at
                                                                ? new Date(row.created_at).toLocaleString("pt-PT")
                                                                : "—"}
                                                        </td>
                                                        <td>
                                                            {row.status === "pending" ? (
                                                                <div className="d-flex align-items-center" style={{ gap: 6, flexWrap: "wrap" }}>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-success"
                                                                        disabled={resolvingId === row.id || !row.available_qty}
                                                                        onClick={() => resolve(row, "restore")}
                                                                    >
                                                                        Repor todas
                                                                    </button>
                                                                    <input
                                                                        type="number"
                                                                        min={1}
                                                                        max={row.available_qty || 0}
                                                                        className="form-control form-control-sm"
                                                                        style={{ width: 70 }}
                                                                        placeholder="N"
                                                                        value={partialQty[row.id] || ""}
                                                                        onChange={(e) =>
                                                                            setPartialQty((p) => ({ ...p, [row.id]: e.target.value }))
                                                                        }
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        disabled={resolvingId === row.id || !row.available_qty}
                                                                        onClick={() => resolve(row, "partial")}
                                                                    >
                                                                        Repor parcial
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-secondary"
                                                                        disabled={resolvingId === row.id}
                                                                        onClick={() => resolve(row, "skip")}
                                                                    >
                                                                        Ignorar
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted">
                                                                    {row.resolved_qty != null
                                                                        ? `${row.resolved_qty} reposta(s)`
                                                                        : "—"}
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className="fuelCF">
                                    <div className="card-footer card-footer-part clearfix">
                                        <div className="pagination pagination-sm m-0 float-right">
                                            <ReactPaginate
                                                previousLabel={<i className="bi bi-chevron-double-left"></i>}
                                                nextLabel={<i className="bi bi-chevron-double-right"></i>}
                                                breakLabel={"..."}
                                                pageCount={Math.max(1, Math.ceil(total / PAGE_SIZE))}
                                                marginPagesDisplayed={2}
                                                pageRangeDisplayed={5}
                                                onPageChange={handlePage}
                                                forcePage={page - 1}
                                                containerClassName={"pagination"}
                                                activeClassName={"active"}
                                                pageClassName={"page-item"}
                                                pageLinkClassName={"page-link"}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
